import { type Member, MFAStatus, type SamlConfig } from '../model';

export type ResetMfaActionState = { available: true } | { available: false; reason: string };

/**
 * Determines whether the "Reset MFA" action is available for a member.
 * Returns null when the action should be completely hidden (e.g., pending invite).
 *
 * The action is only available for Basic Auth members with MFA enabled or in reset state.
 * SSO and OAuth members have MFA managed externally. Members with MFA already disabled
 * have nothing to reset.
 */
export function getResetMfaActionState(member: Member, samlConfig?: SamlConfig): ResetMfaActionState | null {
  // Pending invite — no actions available
  if (member._pendingInvite) {
    return null;
  }

  // MFA is already disabled — nothing to reset
  if (member.mfa === MFAStatus.DISABLED) {
    return { available: false, reason: 'MFA is already disabled for this member' };
  }

  // SSO enforced — MFA managed by SSO provider
  // Matches gonfalon's SSOEnabledForAccount (saml.go:204-206): enabled AND requireSso
  if (samlConfig?.enabled && samlConfig?.requireSso) {
    return { available: false, reason: 'MFA is managed by SSO' };
  }

  // OAuth member — MFA managed by OAuth provider
  if (member.oauthProviders && member.oauthProviders.length > 0) {
    return { available: false, reason: 'MFA is managed by the OAuth provider' };
  }

  // Basic Auth member with MFA enabled or in reset state — can reset
  if (member.hasPassword && (member.mfa === MFAStatus.ENABLED || member.mfa === MFAStatus.RESET)) {
    return { available: true };
  }

  // Fallback — no MFA to reset (shouldn't normally reach here)
  return null;
}
